import { useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { OverlayPanel } from 'primereact/overlaypanel';

import { useAppSelector } from '@/app/hooks';
import { AccountAutoComplete } from '@/components';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import {
  BillingSplitLabel,
  BillingSplitType,
  NULL_ACCOUNT_STATE,
  PartnershipTypeValue,
  THIRTY_THREE_PERCENT_STATE,
  TWENTY_FIVE_PERCENT_STATE,
} from '../constants';
import { usePartnershipForm } from '../hooks/usePartnershipForm';
import { PartnershipSummary } from '../PartnershipSummary';

type PartnershipFormFormProps = {
  label?: string;
  className?: string;
  type: number;
  viewOnly?: boolean;
  showPercentages?: boolean;
  showBillingSplit?: boolean;
};

export const PartnershipFormForm = ({
  className,
  type,
  viewOnly = false,
  showPercentages = true,
  showBillingSplit = true,
}: PartnershipFormFormProps) => {
  const {
    tempAccounts,
    tempPercents,
    setTempPercents,
    setTempAccounts,
    handleAccountSelect,
    handleSaveAccountPositions,
    currentPartnershipItem,
    viewOnly: isViewOnly,
  } = usePartnershipForm(type, viewOnly);
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const isPartnershipUpdateEnabled = useFeatureFlag('Partnership_Update');

  const partnershipAccountOptions = useMemo(() => {
    if (!currentPartnershipItem) return [];
    const seen = new Set<string>();
    const options: NonNullable<typeof currentPartnershipItem.account1>[] = [];
    ([1, 2, 3, 4] as const).forEach((n) => {
      const acc =
        currentPartnershipItem[
          `account${n}` as keyof typeof currentPartnershipItem
        ];
      if (
        acc &&
        typeof acc === 'object' &&
        'id' in acc &&
        acc.id &&
        !seen.has(acc.id as string)
      ) {
        seen.add(acc.id as string);
        options.push(
          acc as NonNullable<typeof currentPartnershipItem.account1>,
        );
      }
    });
    return options;
  }, [currentPartnershipItem]);

  const [selectedSplit, setSelectedSplit] = useState<BillingSplitType>(
    BillingSplitType.TWENTY_FIVE_EACH,
  );

  const [originalSplitType, setOriginalSplitType] =
    useState<BillingSplitType | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const overlayPanelRef = useRef<OverlayPanel>(null);

  const getBillingSplitDistribution = (splitType: BillingSplitType) => {
    switch (splitType) {
      case BillingSplitType.FIFTY_FIFTY:
        return 'Partner A gets 2 options, Partner B gets 2 options';
      case BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE:
        return 'Partner A gets 3 options, Partner B gets 1 option';
      case BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE:
        return 'Partner A gets 2 options, Partner B gets 1 option, Partner C gets 1 option';
      case BillingSplitType.TWENTY_FIVE_EACH:
        return 'Partner A gets 1 option, Partner B gets 1 option, Partner C gets 1 option, Partner D gets 1 option';
      case BillingSplitType.THIRTY_THREE_EACH:
        return 'Partner A gets 1 option, Partner B gets 1 option, Partner C gets 1 option';
      case BillingSplitType.ONE_HUNDRED:
        return 'Partner A gets 1 option';
      default:
        return '';
    }
  };

  const billingSplitOptions = Object.values(BillingSplitType)
    .filter((value) => value !== BillingSplitType.ONE_HUNDRED)
    .map((value) => ({
      value,
      label: BillingSplitLabel[value],
      distribution: getBillingSplitDistribution(value),
    }));

  const isDonorPartnershipBilling =
    type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING;
  const isSpecialBilling = type === PartnershipTypeValue.SPECIAL_BILLING;
  const isBillAccount = type === PartnershipTypeValue.BILL_ACCOUNT;

  const handleSplitChange = (e: DropdownChangeEvent) => {
    const newSplit = e.value as BillingSplitType;
    setSelectedSplit(newSplit);

    let updatedPercents;
    switch (newSplit) {
      case BillingSplitType.THIRTY_THREE_EACH:
        updatedPercents = THIRTY_THREE_PERCENT_STATE;
        break;
      default:
        updatedPercents = TWENTY_FIVE_PERCENT_STATE;
        break;
    }
    setTempPercents(updatedPercents);
    setTempAccounts(NULL_ACCOUNT_STATE);
  };

  const renderAccountOption = (optionNumber: number) => {
    const accountKey = `account${optionNumber}` as keyof typeof tempAccounts;
    const percentKey = `percent${optionNumber}` as keyof typeof tempPercents;
    const account = tempAccounts[accountKey];
    const percent = tempPercents[percentKey];

    const accountInput =
      isPartnershipUpdateEnabled &&
      isDonorPartnershipBilling &&
      partnershipAccountOptions.length > 0 ? (
        <div className="flex flex-col gap-1">
          <label className="text-sm">{`Option ${optionNumber}`}</label>
          <Dropdown
            value={account}
            options={partnershipAccountOptions}
            onChange={(e: DropdownChangeEvent) =>
              setTempAccounts((prev) => ({
                ...prev,
                [`account${optionNumber}`]: e.value ?? null,
              }))
            }
            optionLabel="name"
            dataKey="id"
            placeholder={`Select option ${optionNumber}`}
            disabled={viewOnly}
            className="w-full"
          />
        </div>
      ) : (
        <AccountAutoComplete
          appendTo={null}
          label={`Option ${optionNumber}`}
          defaultValue={account as any}
          field="name"
          disabled={isBillAccount ? true : viewOnly}
          onSelect={(e) => handleAccountSelect(e, optionNumber)}
        />
      );

    return (
      <div className="flex items-start gap-4">
        <div className="flex-1">{accountInput}</div>
        {showPercentages && (
          <div className="flex w-20 flex-col">
            <label className="text-sm">%</label>
            <InputNumber
              className="[&_.p-inputnumber-input]:w-20 [&_.p-inputnumber-input]:text-center"
              value={isBillAccount && optionNumber === 1 ? 100 : percent}
              mode="decimal"
              min={0}
              max={100}
              step={25}
              minFractionDigits={0}
              maxFractionDigits={0}
              disabled
            />
          </div>
        )}
      </div>
    );
  };

  const shouldShowOption4 = () => {
    if (isBillAccount) return false;
    if (
      isDonorPartnershipBilling &&
      (tempPercents.percent1 === 33 ||
        originalSplitType === BillingSplitType.THIRTY_THREE_EACH)
    )
      return false;
    if (!showBillingSplit) return true;
    return selectedSplit !== BillingSplitType.THIRTY_THREE_EACH;
  };

  const getSaveButtonDisabled = () => {
    if (viewOnly) return true;

    if (
      !workingDonorSchedule?.id ||
      workingDonorSchedule.id === '-1' ||
      workingDonorSchedule.id === ''
    ) {
      return true;
    }

    if (type === PartnershipTypeValue.BILL_ACCOUNT) {
      return true;
    }

    const splitType = getSaveButtonSplitType();
    const requiredSlots =
      splitType === BillingSplitType.THIRTY_THREE_EACH ? 3 : 4;

    const filledAccounts = [
      tempAccounts.account1,
      tempAccounts.account2,
      tempAccounts.account3,
      tempAccounts.account4,
    ].filter((account) => account).length;

    if (filledAccounts < requiredSlots) {
      return true;
    }

    return false;
  };

  const getSaveButtonSplitType = (): BillingSplitType => {
    if (type === PartnershipTypeValue.BILL_ACCOUNT) {
      return BillingSplitType.ONE_HUNDRED;
    }

    if (type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING) {
      return originalSplitType ?? selectedSplit;
    }
    return selectedSplit;
  };

  const getStrawOptionsText = () => {
    const splitType = getSaveButtonSplitType();

    let options: string[] = [];

    if (splitType === BillingSplitType.ONE_HUNDRED) {
      options = ['1,5', '2,6', '3,7', '4,8'];
    } else if (splitType === BillingSplitType.FIFTY_FIFTY) {
      options = ['1,5', '2,6', '3,7', '4,8'];
    } else if (splitType === BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE) {
      options = ['1,5', '2,6', '3,7', '4,8'];
    } else if (splitType === BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE) {
      options = ['1,5', '2,6', '3,7', '4,8'];
    } else if (splitType === BillingSplitType.TWENTY_FIVE_EACH) {
      options = ['1,5', '2,6', '3,7', '4,8'];
    } else if (splitType === BillingSplitType.THIRTY_THREE_EACH) {
      options = ['1,4,7', '2,5,8', '3,6'];
    }

    return options.map(
      (straws, index) => `Option ${index + 1}: straws ${straws}, etc.`,
    );
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    try {
      await handleSaveAccountPositions(getSaveButtonSplitType()!);
    } finally {
      setIsSaving(false);
    }
  };

  const getSaveButtonHelperText = () => {
    if (
      !workingDonorSchedule?.id ||
      workingDonorSchedule.id === '-1' ||
      workingDonorSchedule.id === ''
    ) {
      return 'Save the donor schedule first before creating a partnership';
    }
    return 'All options must be filled in';
  };

  return (
    <div className={`flex w-full flex-col gap-8 ${className}`.trim()}>
      <PartnershipSummary
        currentPartnershipItem={currentPartnershipItem}
        originalSplitType={originalSplitType}
        setOriginalSplitType={setOriginalSplitType}
      />

      {isSpecialBilling && showBillingSplit && (
        <div className="flex flex-col gap-2">
          <label className="font-medium">Billing Split</label>
          <Dropdown
            value={selectedSplit}
            options={billingSplitOptions}
            onChange={handleSplitChange}
            optionLabel="label"
            placeholder="Select a billing split"
            disabled={viewOnly}
            valueTemplate={(option) => (
              <div className="flex flex-col">
                <span>{option.label}</span>
                <span className="text-xs text-gray-500">
                  ({option.distribution})
                </span>
              </div>
            )}
            itemTemplate={(option) => (
              <div className="flex flex-col">
                <span>{option.label}</span>
                <span className="text-xs text-gray-500">
                  ({option.distribution})
                </span>
              </div>
            )}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-4">
          {renderAccountOption(1)}
          {!isBillAccount && renderAccountOption(2)}
        </div>

        {!isBillAccount && (
          <div className="flex flex-col gap-4">
            {renderAccountOption(3)}
            {shouldShowOption4() && renderAccountOption(4)}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-4">
        <Button
          label="Save Partnership"
          onClick={handleSaveClick}
          loading={isSaving}
          disabled={getSaveButtonDisabled()}
          className="w-48"
        />
        <p className="m-0 text-sm text-gray-500 italic">
          {getSaveButtonHelperText()}
        </p>
        <div className="flex items-center gap-2">
          <i
            className="pi pi-question-circle cursor-pointer text-lg text-blue-500"
            onClick={(e) => overlayPanelRef.current?.toggle(e)}
          />
          <span className="text-sm text-gray-600">Straw distribution help</span>
        </div>
      </div>

      <OverlayPanel ref={overlayPanelRef} className="w-80">
        <div className="p-4">
          <h3 className="mb-3 font-semibold text-gray-800">
            Straw Distribution
          </h3>
          <div className="space-y-2">
            {getStrawOptionsText().map((option, index) => (
              <div key={index} className="text-sm text-gray-700">
                {option}
              </div>
            ))}
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
};
